import * as React from "react";
import { createRoot } from "react-dom/client";
import moment from "moment";
import { RouterProvider } from "react-router-dom";
import router from "./routes/index.jsx";
import { GTMProvider } from "@elgorditosalsero/react-gtm-hook";
import "@fontsource-variable/cabin/index.css";
import "@fontsource-variable/open-sans/index.css";
import "@fontsource/material-icons";
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-sans/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/600.css";
import "@fontsource/jetbrains-mono/700.css";
/////////// Theme Framework ////////////////
import { CustomThemeProvider } from "themes/CustomThemeProvider";

/////////// Providers ////////////////
import { SupabaseAuthProvider } from "contexts/SupabaseAuthContext";
import { NotificationProvider } from "contexts/NotificationContext";
import { AxiosProvider } from "services/axios-supabase";

import NavigationScroll from "layout/NavigationScroll";
import { Provider } from "react-redux";
import store from "./store/store";
import { ReactErrorBoundary } from "components/ErrorBoundary";

const rootElement = document.getElementById("root");

// Remove chunk-recovery cache-bust param after load (see utils/lazyWithRecovery.js).
try {
  const u = new URL(window.location.href);
  if (u.searchParams.has("fr")) {
    u.searchParams.delete("fr");
    window.history.replaceState(null, "", u.pathname + u.search + u.hash);
  }
} catch {
  /* ignore */
}

const root = createRoot(rootElement);

/** Shared LCCTO GTM container — stephansmith.me, fractional.tools, app.fractional.tools */
const gtmId = import.meta.env.VITE_GOOGLE_ANALYTICS_TRACKING_ID;
const gtmParams = gtmId ? { id: gtmId } : null;

/////////// Providers ////////////////

import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    Sentry.browserTracingIntegration(),
  ],
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "local",
  telemetry: false,
  enableLogs: true,
  tracePropagationTargets: [
    "localhost",
    "staging.api.fractional.tools",
    "api.fractional.tools",
  ],
  tracesSampleRate: process.env.NODE_ENV === "production" ? 0.2 : 1.0,
});

// Add to the window object for debugging.
window.Sentry = Sentry;

// package.json version (injected in vite.config) — also on window for quick DevTools check.
const appVersion = import.meta.env.VITE_APP_VERSION;
window.__FRACTIONAL_UI_VERSION__ = appVersion;
console.log(
  "%c[fractional-ui] v%s",
  "font-weight:600;color:#0d9488",
  appVersion
);

// Global error handler for unhandled errors
window.addEventListener("error", (event) => {
  console.error("Global error caught:", event.error);
  if (process.env.NODE_ENV === "development") {
    console.error("Error details:", {
      message: event.error?.message,
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  }
});

// Global promise rejection handler
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);
  if (process.env.NODE_ENV === "development") {
    console.error("Promise rejection details:", event.reason);
  }
});

/////////// Providers ////////////////

moment.updateLocale("en", { week: { dow: 1 } });

const appTree = (
  <SupabaseAuthProvider>
    <AxiosProvider>
      <CustomThemeProvider>
        <NavigationScroll>
          <NotificationProvider>
            <ReactErrorBoundary>
              <RouterProvider router={router} />
            </ReactErrorBoundary>
          </NotificationProvider>
        </NavigationScroll>
      </CustomThemeProvider>
    </AxiosProvider>
  </SupabaseAuthProvider>
);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      {gtmParams ? (
        <GTMProvider state={gtmParams}>{appTree}</GTMProvider>
      ) : (
        appTree
      )}
    </Provider>
  </React.StrictMode>
);

// Register the service worker if supported
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    // Register with cache busting to ensure we get the latest version
    navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then((registration) => {
        console.log(
          "Service Worker registered with scope:",
          registration.scope
        );

        // Check for updates immediately
        registration.update();

        // Check for updates periodically (every hour)
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);

        // Listen for updates
        registration.addEventListener("updatefound", () => {
          console.log("🔄 New service worker found, installing...");
          const newWorker = registration.installing;

          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed") {
              if (navigator.serviceWorker.controller) {
                // New service worker is ready, but old one is still active
                console.log("✅ New service worker installed. It will activate on next page load.");
                console.log("💡 Reload the page to activate the new service worker.");
                
                // Optionally, you could show a notification to the user here
                // or automatically reload the page
                // window.location.reload();
              } else {
                // First time installation
                console.log("✅ Service worker installed for the first time.");
              }
            }
          });
        });

        // Listen for controller change (when new service worker takes over)
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          console.log("🔄 Service worker controller changed - new version is active!");
          // Optionally reload the page to ensure everything is fresh
          // window.location.reload();
        });
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
  });

  // Also check for updates when the page becomes visible again
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && navigator.serviceWorker.controller) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) {
          registration.update();
        }
      });
    }
  });

  // Expose service worker update function globally for debugging
  window.updateServiceWorker = async () => {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        console.log("🔄 Forcing service worker update...");
        await registration.update();
        console.log("✅ Update check complete");
        return true;
      }
    }
    console.error("❌ Service worker not available");
    return false;
  };

  // Expose service worker unregister function globally for debugging
  window.unregisterServiceWorker = async () => {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        console.log("🗑️ Unregistering service worker...");
        const unregistered = await registration.unregister();
        console.log(unregistered ? "✅ Service worker unregistered" : "❌ Failed to unregister");
        return unregistered;
      }
    }
    console.error("❌ Service worker not available");
    return false;
  };
}

console.info(`Version: 1.0.0`);
// console.log("BUILD:", import.meta.env.VITE_BUILD_TIME);
